// Queries load from a generated static registry (`pnpm gen:queries`) holding each query's verbatim
// text — no bundler loader, Turbopack-compatible. RESULT types come from the generated
// `QueryTypeMap` (`pnpm gen:query-types`); the runtime only sends the verbatim registry text, so the
// map is types-only. A known query path resolves to its typed result; any other string (incl. the
// codegen-excluded `qInfopageDetail.graphql`) or an explicit generic falls back to `<T = Json>`.
import type { Json, QueryServerSideResult } from '@/types/domain';
import type { QueryTypeMap } from '@/queries/queryTypes.generated';
import { queryRegistry } from '@/queries/queryRegistry.generated';

const flattenCkeditorFields = (node: Json): Json => {
  if (Array.isArray(node))
    return node.map((item) => flattenCkeditorFields(item));
  if (node && typeof node === 'object') {
    const keys = Object.keys(node);
    if (keys.length === 1 && keys[0] === 'html')
      return (node as { html: Json }).html;
    const out: Record<string, Json> = {};
    for (const k of keys) out[k] = flattenCkeditorFields(node[k]);
    return out;
  }
  return node;
};

// Overload 1: a known query path infers its typed result.
function queryServerSideData<K extends keyof QueryTypeMap>(
  queryPath: K,
  variables?: Record<string, unknown>,
  cookies?: string,
): Promise<QueryServerSideResult<QueryTypeMap[K]['result']>>;
// Overload 2: any other path / an explicit generic falls back to Json (or the given T).
function queryServerSideData<T = Json>(
  queryPath: string,
  variables?: Record<string, unknown>,
  cookies?: string,
): Promise<QueryServerSideResult<T>>;
async function queryServerSideData<T = Json>(
  queryPath: string,
  variables: Record<string, unknown> = {},
  cookies = '',
): Promise<QueryServerSideResult<T>> {
  const apiUrl = process.env.NEXT_PUBLIC_CRAFTCMS_API;
  const query = (queryRegistry as Record<string, string | undefined>)[
    queryPath
  ];
  if (!query) throw new Error(`Unknown GraphQL query: ${queryPath}`);

  const queryData = {
    query,
    variables,
  };

  const opts = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      cookie: cookies,
    },
    body: JSON.stringify(queryData),
  };

  let json: QueryServerSideResult<T> =
    false as unknown as QueryServerSideResult<T>;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const res = await fetch(apiUrl!, opts);

  if (res.ok) {
    json = (await res.json()) as QueryServerSideResult<T>;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    if (json?.data) json.data = flattenCkeditorFields(json.data) as T;
  } else {
    json = { error: true, status: res.status, message: res.statusText };
  }

  return json;
}

export default queryServerSideData;
